"use client";

import { useState } from "react";
import { CheckIcon, ChevronDownIcon, Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/shadcn/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/shadcn/popover";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { useSpotUser } from "@/utils/spotUser";
import type { RowsResponse, TableSelectSource } from "@/generated";
import { useDebouncedCallback } from "./useDebouncedCallback";

const PAGE_SIZE = 25;

type LookupRow = Record<string, unknown>;

/**
 * Async searchable dropdown for a table-lookup select cell. Options are
 * fetched server-side (search + limit) from the configured DataTable, so
 * catalogs with thousands of rows never reach the client. Selecting an
 * option hands the caller the FULL matched row so it can copy fill-mapped
 * sibling values in the same commit.
 */
export const LookupCellCombobox = ({
  source,
  value,
  onSelect,
  readOnly,
}: {
  source: TableSelectSource;
  value: unknown;
  onSelect: (value: string | null, row: LookupRow | null) => void;
  readOnly?: boolean;
}) => {
  const { spotGroup } = useSpotUser();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [term, setTerm] = useState("");
  const debouncedSetTerm = useDebouncedCallback<string>(setTerm, 300);

  const { data, isLoading } = useRemoteData(
    DATA_KEYS.TABLE_ROWS,
    {
      path: { group_id: spotGroup.id, table_id: source.table_id },
      query: { limit: PAGE_SIZE, search: term || undefined },
    },
    { enabled: open && Boolean(source.table_id) }
  );
  const rows = ((data as RowsResponse | undefined)?.rows ?? []) as LookupRow[];

  const stringValue = value != null && value !== "" ? String(value) : "";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={readOnly}
          className="h-8 w-full flex items-center justify-between gap-2 px-3 text-sm bg-transparent hover:bg-muted/30 transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span
            className={cn("truncate", !stringValue && "text-muted-foreground")}
          >
            {stringValue || "-"}
          </span>
          <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search..."
            value={input}
            onValueChange={(v) => {
              setInput(v);
              debouncedSetTerm(v);
            }}
          />
          <CommandList>
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <CommandEmpty>No matches.</CommandEmpty>
                <CommandGroup>
                  {stringValue && (
                    <CommandItem
                      className="cursor-pointer"
                      value="__clear__"
                      onSelect={() => {
                        onSelect(null, null);
                        setOpen(false);
                      }}
                    >
                      <span className="text-muted-foreground">Clear</span>
                    </CommandItem>
                  )}
                  {rows.map((row, i) => {
                    const optionValue =
                      row[source.value_column] != null
                        ? String(row[source.value_column])
                        : "";
                    if (!optionValue) return null;
                    return (
                      <CommandItem
                        className="cursor-pointer"
                        key={`${optionValue}-${i}`}
                        value={optionValue}
                        onSelect={() => {
                          onSelect(optionValue, row);
                          setOpen(false);
                        }}
                      >
                        <span className="truncate">{optionValue}</span>
                        {optionValue === stringValue && (
                          <CheckIcon className="ml-auto h-4 w-4" />
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
